import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "@reach/router";
import { ToastContainer, toast } from "react-toastify";
import type { BannerType } from "@ifgengineering/client-invest-sdk";
import PageContainer from "../components/pageContainer/PageContainer";
import NavBar from "../components/navbar";
import Footer from "../components/footer/Footer";
import { CompleteOnboardingBanner } from "../components/deals/completeOnboarding";
import "react-toastify/dist/ReactToastify.css";
import {
  hasKYCStartedSelector,
  investorSelector,
  neverLoadedInvestorSelector,
} from "../state/investor/investor.selectors";
import TutorialModal from "../components/TutorialModal";
import { VerifyIdentityBanner } from "../components/deals/verifyIndentity/VerifyIdentityBanner";
import { PageContentContainer } from "@components/pageContainer/styled";
import { NavBarContainer, PageWrapper } from "./styled";
import RegulatoryBanner from "@components/RegulatoryBanner/RegulatoryBanner";
import ViewAsBanner from "@components/ViewAsBanner/ViewAsBanner";
import { InlineLoader } from "@components/Loading/InlineLoader";
import VerifyInvestorProfileBanner from "../components/deals/verifyInvestorProfile/VerifyInvestorProfileBanner";
import VerifyAccountBanner from "../components/deals/verifyAccount/VerifyAccountBanner";
import { TaxResidencyBanner } from "../components/TaxResidencyBanner/TaxResidencyBanner";
import { DealOnboardingPromptProvider } from "@components/OnboardingV2B/DealOnboardingPrompt";
import { FooterContentProvider } from "@components/footer/FooterContentContext";
import { useWebOnboardingV2B } from "@components/OnboardingV2B/useWebOnboardingV2B";

const VerifyInvestorProfileModal = React.lazy(
  () =>
    import(
      "../components/deals/verifyInvestorProfile/VerifyInvestorProfileModal"
    )
);

const VerifyAccountModal = React.lazy(
  () => import("../components/deals/verifyAccount/VerifyAccountModal")
);

// TODO: Remove this layout component when we finish with the frontend refractory
// Add more pages to this array as you refactor then
const layoutlessPages = [
  "/signup",
  "/app/identity/*",
  "/app/login",
  "/app/signup",
  "/app/signup-apple",
  "/app/onboarding/*",
  "/app/onboarding-v2b/*",
  "/app/forgot-password",
  "/app/reset-password",
  "/app/verify-email",
];

export const localStorageTutorialKey = "hasSeenTutorial";

type LayoutProps = {
  hideFooter?: boolean;
};

const LayoutContent: React.FC<LayoutProps> = ({
  children,
  hideFooter = false,
}) => {
  const hasSeenTutorial = localStorage.getItem(localStorageTutorialKey);
  const [showTutorialModal, setShowTutorialModal] = useState<boolean>(
    hasSeenTutorial === "false"
  );
  const [showHowItWorksModal, setShowHowItWorksModal] =
    useState<boolean>(false);

  useEffect(() => {
    setShowTutorialModal(hasSeenTutorial === "false");
  }, [hasSeenTutorial]);

  const noLayout = layoutlessPages.some((route) => useMatch(route));
  const investor = useSelector(investorSelector);
  const neverLoadedInvestor = useSelector(neverLoadedInvestorSelector);
  const isKYCPending = !useSelector(hasKYCStartedSelector);
  const bannerType = investor?.bannerType;
  const showBanner = !!bannerType && !neverLoadedInvestor;
  // Deal-anchored onboarding never plays the welcome video, before or after identity.
  const { isActive: isDealAnchoredOnboarding } = useWebOnboardingV2B();

  const [renderVerifyProfileModal, setRenderVerifyProfileModal] =
    useState<boolean>(false);
  const [renderVerifyAccountModal, setRenderVerifyAccountModal] =
    useState<boolean>(false);

  const renderBanner = (bannerType: BannerType) => {
    switch (bannerType) {
      case "VERIFY_EMAIL":
        return (
          <VerifyAccountBanner
            onClickCTA={() => setRenderVerifyAccountModal(true)}
          />
        );
      case "COMPLETE_ONBOARDING":
        return <CompleteOnboardingBanner />;
      case "VERIFY_IDENTITY":
        return <VerifyIdentityBanner verificationInProgress={!isKYCPending} />;
      case "VERIFY_INVESTOR_PROFILE":
        return (
          <VerifyInvestorProfileBanner
            onClickCTA={() => {
              setRenderVerifyProfileModal(true);
            }}
          />
        );
      case "ADD_TAX_RESIDENCY":
        return <TaxResidencyBanner />;
      default:
        return null;
    }
  };

  if (noLayout) {
    return (
      <>
        <ToastContainer
          autoClose={5000}
          closeOnClick
          hideProgressBar={false}
          position={toast.POSITION.TOP_CENTER}
        />
        <ViewAsBanner />
        {children}
      </>
    );
  }

  return (
    <PageWrapper>
      <TutorialModal
        title="Welcome to Cur8"
        description="Use our platform to Invest in institutional grade investments, track your portfolio and access a range of additional tools to help you manage your wealth effortlessly."
        buttonText="Get Started"
        videoUrl="https://www.youtube.com/embed/CpA8J4fcIJg?si=7lQf7Yf4QNP5bqb3"
        show={showTutorialModal && !isDealAnchoredOnboarding}
        onClose={() => {
          localStorage.setItem(localStorageTutorialKey, "true");
          setShowTutorialModal(false);
        }}
      />
      <TutorialModal
        title="Invest in institutional grade investments"
        description="Access our carefully curated investments across V.C, real estate, fixed income and private equity"
        buttonText="Get Started"
        videoUrl="https://player.vimeo.com/video/743501081?h=6c36b9b9d2&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
        show={showHowItWorksModal}
        onClose={() => setShowHowItWorksModal(false)}
      />
      <Suspense fallback={<InlineLoader />}>
        <VerifyAccountModal
          show={renderVerifyAccountModal}
          onClose={() => {
            setRenderVerifyAccountModal(false);
          }}
        />
      </Suspense>
      {bannerType === "VERIFY_INVESTOR_PROFILE" && (
        <Suspense fallback={<InlineLoader />}>
          <VerifyInvestorProfileModal
            show={renderVerifyProfileModal}
            onClose={() => {
              setRenderVerifyProfileModal(false);
            }}
          />
        </Suspense>
      )}
      <ToastContainer
        autoClose={5000}
        closeOnClick
        hideProgressBar={false}
        position={toast.POSITION.TOP_CENTER}
      />
      <PageContainer>
        {/* Inside the page, not above it: the scrolling happens within
            PageContainer, so a sibling above it would stay pinned. */}
        <ViewAsBanner />
        <RegulatoryBanner />
        {showBanner && renderBanner(bannerType)}
        <NavBarContainer>
          <NavBar showTutorialModal={() => setShowHowItWorksModal(true)} />
        </NavBarContainer>
        <PageContentContainer>{children}</PageContentContainer>
        {!hideFooter && <Footer />}
      </PageContainer>
    </PageWrapper>
  );
};

const Layout: React.FC<LayoutProps> = (props) => (
  <DealOnboardingPromptProvider>
    <FooterContentProvider>
      <LayoutContent {...props} />
    </FooterContentProvider>
  </DealOnboardingPromptProvider>
);

export default Layout;
